import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { t, i18n } from '../core/i18n.js';
import { popToast } from '../utils/alerts.js';

const MAX_FICHIERS = 10;
window.fichiersEnAttente = [];

function _renderFilePreviewBanner() {
    const banner = document.getElementById('file-preview-banner');
    const list   = document.getElementById('file-preview-list');
    if (!banner || !list) return;

    if (window.fichiersEnAttente.length === 0) {
        banner.style.display = 'none';
        return;
    }

    banner.style.display = 'flex';
    list.innerHTML = '';

    window.fichiersEnAttente.forEach((file, index) => {
        const chip = document.createElement('div');
        chip.className = 'file-chip';

        if (file.type.startsWith('image/')) {
            const img = document.createElement('img');
            img.className = 'file-chip-thumb';
            const _thumbUrl = URL.createObjectURL(file);
            img.src = _thumbUrl;
            img.alt = '';
            URL.revokeObjectURL(_thumbUrl);
            chip.appendChild(img);
        } else {
            const icon = document.createElement('i');
            icon.className = `fas fa-${file.type.startsWith('video/') ? 'film' : 'file-archive'} file-chip-icon`;
            chip.appendChild(icon);
        }

        const info = document.createElement('div');
        info.className = 'file-chip-info';
        const name = document.createElement('span');
        name.className = 'file-chip-name';
        name.textContent = file.name.length > 22 ? file.name.substring(0, 19) + '…' : file.name;
        name.title = file.name;
        const size = document.createElement('span');
        size.className = 'file-chip-size';
        size.textContent = (file.size / 1024 / 1024).toFixed(2) + ' MB';
        info.appendChild(name);
        info.appendChild(size);
        chip.appendChild(info);

        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'file-chip-del';
        del.textContent = '✖';
        del.title = 'Retirer';
        del.onclick = () => annulerFichier(index);
        chip.appendChild(del);

        list.appendChild(chip);
    });
}

export function initDragAndDrop() {
    const dropOverlay = document.getElementById('drop-zone-overlay');
    if (!dropOverlay) return;

    window.addEventListener('dragenter', (e) => {
        e.preventDefault();
        if (window._dragId) return;
        if (e.dataTransfer.types.includes('Files')) {
            dropOverlay.style.display = 'flex';
        }
    });

    dropOverlay.addEventListener('dragleave', () => {
        dropOverlay.style.display = 'none';
    });

    window.addEventListener('dragover', (e) => e.preventDefault());

    window.addEventListener('drop', (e) => {
        e.preventDefault();
        dropOverlay.style.display = 'none';
        if (window._dragId) return;
        for (const file of e.dataTransfer.files) {
            mettreFichierEnAttente(file);
        }
    });
}

export function mettreFichierEnAttente(file) {
    if (!file) return;
    if (window.fichiersEnAttente.length >= MAX_FICHIERS) {
        popToast(`Maximum ${MAX_FICHIERS} fichiers par message.`, 'error');
        return;
    }
    window.fichiersEnAttente.push(file);
    _renderFilePreviewBanner();
    document.getElementById('input')?.focus();
}

export function annulerFichier(index) {
    if (typeof index === 'number') {
        window.fichiersEnAttente.splice(index, 1);
    } else {
        window.fichiersEnAttente = [];
    }
    _renderFilePreviewBanner();
}

export async function envoyerFichierAvecTexte(file, texte, silent = false) {
    if (file.size > 10 * 1024 * 1024) {
        if (!silent) popToast(t('toast.file_too_large'), 'error');
        return false;
    }

    const formData = new FormData();
    formData.append('chatFile', file);
    formData.append('auteur', state.monPseudo);
    formData.append('salonId', state.salonActuelID);
    formData.append('destinataire', state.destinataireID || "public");
    if (texte) formData.append('texteAccompagnement', texte);

    try {
        if (!silent) popToast(t('toast.uploading_file'), 'stream');

        const res = await fetch('/api/upload-chat-file', { method: 'POST', body: formData });
        if (!res.ok && !res.headers.get('content-type')?.includes('application/json')) {
            throw new Error("Crash serveur (500).");
        }

        const data = await res.json();

        if (data.success) {
            if (!silent) {
                popToast(t('toast.file_sent'), 'success');
                annulerFichier();
                const input = document.getElementById('input');
                if (input) { input.value = ''; input.style.height = 'auto'; }
                const counter = document.getElementById('char-counter');
                if (counter) counter.style.display = 'none';
                if (typeof window.annulerReponse === 'function') window.annulerReponse();
                if (typeof window._majSaisie === 'function') window._majSaisie();
            }
            return true;
        } else {
            if (!silent) {
                if (data.error === "ERROR_TROLL") {
                    const trolls = i18n.tArray('upload.trolls');
                    popToast(trolls[Math.floor(Math.random() * trolls.length)], 'error');
                } else {
                    popToast(t('upload.error', { msg: data.error }), 'error');
                }
            }
            return false;
        }
    } catch (err) {
        console.error("❌ Erreur Upload :", err);
        if (!silent) popToast(t('upload.error', { msg: "Connexion perdue avec la Matrice." }), 'error');
        return false;
    }
}

// Envoi d'un fichier en MP chiffré de bout en bout. Calqué sur le chemin
// vocal (voiceRecorder.envoyerVocalE2EE) : le fichier est chiffré AES-GCM
// côté client, uploadé en octets opaques via /api/upload-encrypted-blob
// (jamais en base, jamais broadcasté), et le descripteur — qui contient la
// clé/iv — voyage dans le canal Olm via envoyerMPE2EE. Le serveur ne voit
// jamais le clair ni la clé. Retourne true si envoyé, false sinon.
export async function envoyerFichierMPE2EE(file, texte) {
    if (file.size > 10 * 1024 * 1024) {
        popToast(t('toast.file_too_large'), 'error');
        return false;
    }
    const abToB64 = (buf) => {
        const b = new Uint8Array(buf); let s = '';
        for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
        return btoa(s);
    };
    try {
        popToast(t('toast.uploading_file'), 'stream');
        const key   = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
        const iv    = crypto.getRandomValues(new Uint8Array(12));
        const clair = await file.arrayBuffer();
        const ciffre = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, clair);
        const rawKey = await crypto.subtle.exportKey('raw', key);

        const fd = new FormData();
        fd.append('blob', new Blob([ciffre], { type: 'application/octet-stream' }), 'f.enc');
        const res = await fetch('/api/upload-encrypted-blob', { method: 'POST', body: fd });
        if (!res.ok) { popToast(t('upload.network_error'), 'error'); return false; }
        const up = await res.json();
        if (!up.success) { popToast(t('upload.error', { msg: up.error || '?' }), 'error'); return false; }

        const mime = file.type || 'application/octet-stream';
        let kind = 'chaosFile';
        if (mime.startsWith('image/')) kind = 'chaosImage';
        else if (mime.startsWith('video/')) kind = 'chaosVideo';
        else if (mime.startsWith('audio/')) kind = 'chaosVoice';

        const descripteur = JSON.stringify({
            [kind]: 1, e2ee: 1,
            url: up.url,
            mime,
            name: file.name || '',
            caption: texte || '',
            key: abToB64(rawKey),
            iv: abToB64(iv.buffer)
        });

        const r = await window.envoyerMPE2EE(descripteur);
        if (r === 'sent') { popToast(t('toast.file_sent'), 'success'); return true; }
        popToast(t('upload.error', { msg: '?' }), 'error');
        return false;
    } catch (err) {
        console.error('[Fichier E2EE] Chiffrement/upload échoué :', err);
        popToast(t('upload.error', { msg: 'Connexion perdue avec la Matrice.' }), 'error');
        return false;
    }
}

export async function envoyerAvatarAuServeur(inputElement) {
    const fichier = inputElement.files[0];
    if (!fichier) return;
    ouvrirEditeurAvatar(fichier);
    inputElement.value = '';
}

async function _uploadAvatarBlob(blob) {
    const formData = new FormData();
    formData.append('avatarImage', blob, 'avatar.png');
    formData.append('pseudo', state.monPseudo);

    try {
        popToast(t('toast.avatar_uploading'), "stream");
        const response = await fetch('/api/upload-avatar', { method: 'POST', body: formData });
        if (!response.ok) { popToast(t('upload.network_error'), "error"); return; }
        const data = await response.json();
        if (data.success) {
            popToast(t('toast.avatar_saved'), "success");
            socket.emit('get-user-profile', state.monPseudo);
            socket.emit('demander-liste-maj');
        } else {
            popToast(t('upload.error', { msg: data.error }), "error");
        }
    } catch (err) {
        console.error("Erreur réseau :", err);
        popToast(t('upload.network_error'), "error");
    }
}

// ─── Éditeur d'avatar (crop + zoom + pan) ────────────────────────────────────

const CROP_SIZE = 280;   // taille du canvas affiché
const CROP_RADIUS = 120; // rayon du cercle de découpe (en px canvas)
const OUTPUT_SIZE = 256; // taille de l'image finale exportée

const _crop = {
    img: null, scale: 1, minScale: 1,
    ox: 0, oy: 0,          // offset (centre image → centre canvas)
    dragging: false, lx: 0, ly: 0,
    animId: null
};
let _cropMouseMove = null;
let _cropMouseUp   = null;

function ouvrirEditeurAvatar(fichier) {
    const modal = document.getElementById('avatar-crop-modal');
    const canvas = document.getElementById('avatar-crop-canvas');
    if (!modal || !canvas) return;

    const img = new Image();
    const _cropObjectUrl = URL.createObjectURL(fichier);
    img.onload = () => {
        URL.revokeObjectURL(_cropObjectUrl);
        _crop.img = img;
        _crop.scale = Math.max(
            (CROP_RADIUS * 2) / img.width,
            (CROP_RADIUS * 2) / img.height
        );
        _crop.minScale = _crop.scale;
        _crop.ox = 0;
        _crop.oy = 0;

        const slider = document.getElementById('avatar-zoom-slider');
        if (slider) {
            slider.min = _crop.minScale;
            slider.max = _crop.minScale * 4;
            slider.step = _crop.minScale * 0.01;
            slider.value = _crop.scale;
        }

        canvas.width = CROP_SIZE;
        canvas.height = CROP_SIZE;
        _attachCropEvents(canvas);
        _drawCrop();
        modal.style.display = 'flex';
    };
    img.src = _cropObjectUrl;
}

function _drawCrop() {
    const canvas = document.getElementById('avatar-crop-canvas');
    if (!canvas || !_crop.img) return;
    const ctx = canvas.getContext('2d');
    const cx = CROP_SIZE / 2, cy = CROP_SIZE / 2;

    ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);

    // Image
    const w = _crop.img.width * _crop.scale;
    const h = _crop.img.height * _crop.scale;
    ctx.drawImage(_crop.img, cx + _crop.ox - w / 2, cy + _crop.oy - h / 2, w, h);

    // Voile sombre UNIQUEMENT à l'extérieur du cercle (rect MOINS disque, via
    // remplissage even-odd) → l'image reste nette dans le cercle = aperçu fidèle
    // de l'avatar, et seul le pourtour est assombri.
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.beginPath();
    ctx.rect(0, 0, CROP_SIZE, CROP_SIZE);
    ctx.arc(cx, cy, CROP_RADIUS, 0, Math.PI * 2);
    ctx.fill('evenodd');
    ctx.restore();

    // Bordure du cercle
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, CROP_RADIUS, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
}

function _clampOffset() {
    const hw = (_crop.img.width * _crop.scale) / 2;
    const hh = (_crop.img.height * _crop.scale) / 2;
    const maxX = hw - CROP_RADIUS;
    const maxY = hh - CROP_RADIUS;
    _crop.ox = Math.max(-maxX, Math.min(maxX, _crop.ox));
    _crop.oy = Math.max(-maxY, Math.min(maxY, _crop.oy));
}

function _attachCropEvents(canvas) {
    // Retire les anciens listeners window avant d'en créer de nouveaux
    if (_cropMouseMove) window.removeEventListener('mousemove', _cropMouseMove);
    if (_cropMouseUp)   window.removeEventListener('mouseup',   _cropMouseUp);

    // Retire les anciens listeners canvas en clonant
    const fresh = canvas.cloneNode(true);
    canvas.parentNode.replaceChild(fresh, canvas);
    const c = fresh;

    // Mouse
    c.addEventListener('mousedown', e => {
        _crop.dragging = true;
        _crop.lx = e.clientX; _crop.ly = e.clientY;
    });
    _cropMouseMove = e => {
        if (!_crop.dragging) return;
        _crop.ox += e.clientX - _crop.lx;
        _crop.oy += e.clientY - _crop.ly;
        _crop.lx = e.clientX; _crop.ly = e.clientY;
        _clampOffset();
        _drawCrop();
    };
    _cropMouseUp = () => { _crop.dragging = false; };
    window.addEventListener('mousemove', _cropMouseMove);
    window.addEventListener('mouseup',   _cropMouseUp);

    // Wheel zoom
    c.addEventListener('wheel', e => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 1.07 : 1 / 1.07;
        _crop.scale = Math.max(_crop.minScale, _crop.scale * delta);
        const slider = document.getElementById('avatar-zoom-slider');
        if (slider) slider.value = _crop.scale;
        _clampOffset();
        _drawCrop();
    }, { passive: false });

    // Touch
    let lastDist = null;
    c.addEventListener('touchstart', e => {
        if (e.touches.length === 1) {
            _crop.dragging = true;
            _crop.lx = e.touches[0].clientX; _crop.ly = e.touches[0].clientY;
        }
    });
    c.addEventListener('touchmove', e => {
        e.preventDefault();
        if (e.touches.length === 2) {
            const d = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            if (lastDist !== null) {
                _crop.scale = Math.max(_crop.minScale, _crop.scale * (d / lastDist));
                const slider = document.getElementById('avatar-zoom-slider');
                if (slider) slider.value = _crop.scale;
            }
            lastDist = d;
            _clampOffset();
            _drawCrop();
        } else if (_crop.dragging && e.touches.length === 1) {
            _crop.ox += e.touches[0].clientX - _crop.lx;
            _crop.oy += e.touches[0].clientY - _crop.ly;
            _crop.lx = e.touches[0].clientX; _crop.ly = e.touches[0].clientY;
            _clampOffset();
            _drawCrop();
        }
    }, { passive: false });
    c.addEventListener('touchend', () => { _crop.dragging = false; lastDist = null; });
}

export function onAvatarZoomSlider(val) {
    _crop.scale = Math.max(_crop.minScale, parseFloat(val));
    _clampOffset();
    _drawCrop();
}

export function annulerCropAvatar() {
    if (_cropMouseMove) { window.removeEventListener('mousemove', _cropMouseMove); _cropMouseMove = null; }
    if (_cropMouseUp)   { window.removeEventListener('mouseup',   _cropMouseUp);   _cropMouseUp   = null; }
    const modal = document.getElementById('avatar-crop-modal');
    if (modal) modal.style.display = 'none';
    _crop.img = null;
}

export function confirmerCropAvatar() {
    if (!_crop.img) return;
    const out = document.createElement('canvas');
    out.width = OUTPUT_SIZE; out.height = OUTPUT_SIZE;
    const ctx = out.getContext('2d');

    // Clip circulaire
    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    // Reproduire le même cadrage sur le canvas de sortie
    const ratio = OUTPUT_SIZE / (CROP_RADIUS * 2);
    const w = _crop.img.width * _crop.scale * ratio;
    const h = _crop.img.height * _crop.scale * ratio;
    ctx.drawImage(
        _crop.img,
        OUTPUT_SIZE / 2 + _crop.ox * ratio - w / 2,
        OUTPUT_SIZE / 2 + _crop.oy * ratio - h / 2,
        w, h
    );

    out.toBlob(blob => {
        annulerCropAvatar();
        _uploadAvatarBlob(blob);
    }, 'image/png');
}

export async function envoyerIconeSphereAuServeur(inputElement) {
    const fichier = inputElement.files[0];
    if (!fichier) return;
    if (!state.sphereSelectionneeId) {
        popToast(t('sphere.no_selected'), "error");
        return;
    }

    const formData = new FormData();
    formData.append('sphereImage', fichier); 
    formData.append('sphereId', state.sphereSelectionneeId);

    try {
        popToast(t('toast.sphere_icon_uploading'), "stream");

        const response = await fetch('/api/upload-sphere-icon', {
            method: 'POST',
            body: formData
        });
        if (!response.ok) { popToast(t('upload.network_error'), "error"); return; }
        const data = await response.json();

        if (data.success) {
            popToast(t('toast.sphere_icon_saved'), "success");
            socket.emit('demander-mes-spheres');
        } else {
            popToast(t('upload.error', {msg: data.error}), "error");
        }
    } catch (err) {
        console.error("Erreur réseau :", err);
        popToast(t('upload.network_error'), "error");
    }
}